import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { AssistantMessageConnectIntegration } from "#features/conversations/components/conversation-view/AssistantMessageConnectIntegration.tsx";
import { AssistantMessageConnectServer } from "#features/conversations/components/conversation-view/AssistantMessageConnectServer.tsx";
import { usePendingConnection } from "#features/conversations/hooks/usePendingConnection.ts";
import { getConnectedServerIds, getConnectionOutcome, getConnectionRowStates } from "#features/conversations/lib/connectionRows.ts";
import type { ConnectionRecord } from "#types/ConnectionRecord.ts";
import { useCallback, useEffect, useMemo, useState } from "react";

interface Props {
    conversationId: string;
    connection: ConnectionRecord;
    // The turn that asked has closed, so a resume would not overlap it.
    isSettled: boolean;
}

/**
 * The connect cards for one request, and its pause. While the pause is open, each row can be dismissed, and once every
 * row is connected or dismissed the run resumes on its own. A closed request, as an older card, only offers Connect.
 */
export const AssistantMessageConnection = ({ conversationId, connection, isSettled }: Props) => {
    const { data: channelsDetails } = useGetChannelsDetailsSuspenseQuery();
    const resume = usePendingConnection(conversationId);
    const [dismissedKeys, setDismissedKeys] = useState<ReadonlySet<string>>(new Set());
    const isOpen = connection.status === "pending";
    const { interruptId } = connection;
    const outcome = useMemo(
        () => getConnectionOutcome(getConnectionRowStates(connection, channelsDetails), dismissedKeys),
        [connection, channelsDetails, dismissedKeys]
    );
    const connectedServerIds = useMemo(() => getConnectedServerIds(channelsDetails), [channelsDetails]);

    const dismiss = useCallback((key: string) => setDismissedKeys(keys => new Set(keys).add(key)), []);

    useEffect(() => {
        if (isOpen && isSettled && outcome) {
            resume(interruptId, outcome);
        }
    }, [isOpen, isSettled, outcome, interruptId, resume]);

    const dismissProps = isOpen ? { dismissedKeys, onDismiss: dismiss } : {};

    return (
        <>
            {connection.integrations.length > 0 &&
                <AssistantMessageConnectIntegration integrations={connection.integrations} {...dismissProps} />
            }
            {connection.connectableServers.length > 0 &&
                <AssistantMessageConnectServer servers={connection.connectableServers} connectedServerIds={connectedServerIds} {...dismissProps} />
            }
        </>
    );
};
